import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import { FONT_ENUM } from '../../types/global/fonts.types';
import {
    BRAND_LUXE_PRIMARY,
    BRAND_MODAL_ACCENT_CHIP_BG,
    BRAND_MODAL_ACCENT_CHIP_BORDER,
    BRAND_OUTLINE_VARIANT,
    BRAND_SURFACE_CONTAINER_LOW,
} from './brandDashboardTokens';
import {
    formatUgcHostSubscribeUpsellCurrentRateLine,
    formatUgcHostSubscribeUpsellMessage,
    UGC_CAMPAIGN_HOST_SUBSCRIBE_UPSELL_COPY,
} from './ugcCampaignSubscribeUpsellCopy';
import { MIXPANEL_EVENT, trackSubscriptionAction } from '../../analytics/Mixpanel';

export interface UgcCampaignHostSubscribeBannerProps {
    extraCreators: number;
    currentRateFormatted: string;
    subscribedRateFormatted: string;
    onSubscribe: () => void;
}

export function UgcCampaignHostSubscribeBanner({
    extraCreators,
    currentRateFormatted,
    subscribedRateFormatted,
    onSubscribe,
}: UgcCampaignHostSubscribeBannerProps) {
    const currentRateLine = formatUgcHostSubscribeUpsellCurrentRateLine(currentRateFormatted);
    const message = formatUgcHostSubscribeUpsellMessage(extraCreators, subscribedRateFormatted);

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: 2,
                p: 2,
                borderRadius: 3,
                bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                border: `1px solid ${BRAND_OUTLINE_VARIANT}`,
                backgroundImage: `linear-gradient(90deg, ${BRAND_MODAL_ACCENT_CHIP_BG} 0%, transparent 55%)`,
            }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flex: 1, minWidth: 0 }}>
                <Box
                    sx={{
                        mt: 0.25,
                        width: 36,
                        height: 36,
                        borderRadius: 2,
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: BRAND_MODAL_ACCENT_CHIP_BG,
                        border: `1px solid ${BRAND_MODAL_ACCENT_CHIP_BORDER}`,
                    }}>
                    <WorkspacePremiumIcon sx={{ fontSize: 20, color: BRAND_LUXE_PRIMARY }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            fontSize: 13,
                            lineHeight: 1.5,
                            color: 'rgba(255,255,255,0.62)',
                            mb: 0.5,
                        }}>
                        {currentRateLine}
                    </Typography>
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            fontSize: 14,
                            lineHeight: 1.55,
                            color: 'rgba(255,255,255,0.88)',
                        }}>
                        {message}
                    </Typography>
                </Box>
            </Box>
            <Button
                variant="contained"
                onClick={() => {
                    trackSubscriptionAction(MIXPANEL_EVENT.SUBSCRIBE_CLICK, {
                        buttonName: 'VIEW HOST PLANS',
                        buttonAction: 'SUBSCRIBE',
                        subscriptionType: 'campaign_host',
                        source: 'ugc_host_subscribe_banner',
                    });
                    onSubscribe();
                }}
                sx={{
                    flexShrink: 0,
                    alignSelf: { xs: 'stretch', sm: 'center' },
                    px: 2.5,
                    py: 1,
                    borderRadius: 999,
                    fontFamily: FONT_ENUM.MAIN_BOLD,
                    fontSize: 12,
                    textTransform: 'none',
                    letterSpacing: '0.04em',
                    bgcolor: BRAND_LUXE_PRIMARY,
                    color: '#1a1200',
                    boxShadow: 'none',
                    '&:hover': {
                        bgcolor: '#ffd67a',
                        boxShadow: 'none',
                    },
                }}>
                {UGC_CAMPAIGN_HOST_SUBSCRIBE_UPSELL_COPY.subscribeButtonLabel}
            </Button>
        </Box>
    );
}
